/**
 * Archive Manager Utility
 * Provides Archive, Restore, and Permanent Delete functionality across modules.
 * Archived items are hidden from the mobile app.
 * Suggested actions: Active → Archive → Archived → Restore / Delete
 */

const ARCHIVE_PREFIX = 'gpn_archived_';

export const ArchiveManager = {
  /**
   * Get all archived items for a module
   */
  getArchivedItems: (moduleKey) => {
    try {
      const key = `${ARCHIVE_PREFIX}${moduleKey}`;
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const items = JSON.parse(raw);
      return Array.isArray(items) ? items : [];
    } catch (e) {
      console.error('Error getting archived items:', e);
      return [];
    }
  },

  /**
   * Check if an item is archived
   */
  isArchived: (moduleKey, itemId) => {
    const items = ArchiveManager.getArchivedItems(moduleKey);
    return items.some((item) => String(item.id) === String(itemId));
  },

  /**
   * Archive an item
   * Active → Archive → Archived
   */
  archiveItem: (moduleKey, item) => {
    try {
      const key = `${ARCHIVE_PREFIX}${moduleKey}`;
      const items = ArchiveManager.getArchivedItems(moduleKey);
      const exists = items.some((i) => String(i.id) === String(item.id));
      if (!exists) {
        const archivedEntry = {
          ...item,
          isArchived: true,
          archivedAt: new Date().toISOString(),
          originalStatus: item.status || 'active',
        };
        const updated = [archivedEntry, ...items];
        localStorage.setItem(key, JSON.stringify(updated));
        return { success: true, items: updated };
      }
      return { success: true, items };
    } catch (e) {
      console.error('Error archiving item:', e);
      return { success: false, message: 'Failed to archive item' };
    }
  },

  /**
   * Restore / Unarchive an item
   * Archived → Restore (Active)
   */
  restoreItem: (moduleKey, itemId) => {
    try {
      const key = `${ARCHIVE_PREFIX}${moduleKey}`;
      const items = ArchiveManager.getArchivedItems(moduleKey);
      const filtered = items.filter((i) => String(i.id) !== String(itemId));
      localStorage.setItem(key, JSON.stringify(filtered));
      return { success: true, items: filtered };
    } catch (e) {
      console.error('Error restoring item:', e);
      return { success: false, message: 'Failed to restore item' };
    }
  },

  /**
   * Permanently delete an archived item
   * Archived → Delete
   */
  deletePermanently: (moduleKey, itemId) => {
    try {
      const key = `${ARCHIVE_PREFIX}${moduleKey}`;
      const items = ArchiveManager.getArchivedItems(moduleKey);
      const filtered = items.filter((i) => String(i.id) !== String(itemId));
      localStorage.setItem(key, JSON.stringify(filtered));
      return { success: true, items: filtered };
    } catch (e) {
      console.error('Error permanently deleting item:', e);
      return { success: false, message: 'Failed to delete permanently' };
    }
  },
};

export default ArchiveManager;
